import {
  Box,
  Heading,
  Text,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Grid,
  VStack,
  HStack,
  Badge,
  Button,
  Input,
  FormControl,
  FormLabel,
  Switch,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Tooltip,
  Textarea,
  FormHelperText,
  SimpleGrid,
} from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useState, ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FiSettings,
  FiActivity,
  FiDownload,
  FiEdit,
  FiSlash,
  FiPlus,
  FiSliders,
  FiTarget,
  FiLink,
  FiCreditCard,
  FiGitBranch,
  FiDollarSign,
  FiPercent,
  FiPackage,
  FiUserPlus,
  FiTrash2,
  FiUpload,
  FiClock,
} from 'react-icons/fi'
import {
  useBlocklistEntries,
  useCreateBlocklistEntry,
  useDeleteBlocklistEntry,
  useImportBlocklistEntries,
  useUpdateBlocklistEntry,
} from '@/hooks/useAdminData'
import { triggerBrowserDownload } from '@/services/admin/baseService'
import { blocklistService } from '@/services/admin/blocklistService'
import { useToast } from '@/hooks/useToast'
import { DataTable } from '@/components/common/DataTable'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import type { ColumnConfig, ActionButton } from '@/components/common/DataTable'
import type {
  BlocklistEntry,
  BlocklistImportError,
  BlocklistType,
  BlocklistMatchType,
  BlocklistChainCompatibility,
} from '@/types'
import { usePrivacyPreferencesModal } from '@/contexts/PrivacyPreferencesContext'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { withNetworkStorageKey } from '@/utils/storageKeys'
import { useAdminTabQuery } from '@/navigation/adminTabQuery'
import { MainnetOnlyNotice } from '@/components/common/MainnetOnlyNotice'
import { OnchainOperationPolicyTab } from '@/pages/systemConfig/OnchainOperationPolicyTab'
import { RuntimeConfigTab } from '@/pages/systemConfig/RuntimeConfigTab'
import { RpcEndpointsTab } from '@/pages/systemConfig/RpcEndpointsTab'
import { PlatformWalletsTab } from '@/pages/systemConfig/PlatformWalletsTab'
import { ChainsTab } from '@/pages/systemConfig/ChainsTab'
import { TokensTab } from '@/pages/systemConfig/TokensTab'
import { FeePolicyTab } from '@/pages/systemConfig/FeePolicyTab'
import { SubscriptionPlansTab } from '@/pages/systemConfig/SubscriptionPlansTab'
import { MerchantSignupInvitesTab } from '@/pages/systemConfig/MerchantSignupInvitesTab'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import { CooldownOperationTypesTab } from '@/pages/systemConfig/CooldownOperationTypesTab'
import './systemConfig.css'

const SYSTEM_CONFIG_TABS = [
  'onchain_policies',
  'onchain_policy_overrides',
  'runtime_config',
  'rpc_endpoints',
  'platform_wallets',
  'chains',
  'tokens',
  'fee_policy',
  'subscription_plans',
  'signup_entries',
  'blocklist',
  'cooldown',
  'general',
] as const

// On-chain operation policy UI lives in `OnchainOperationPolicyTab`.

interface BlocklistFormState {
  type: BlocklistType
  matchType: BlocklistMatchType
  value: string
  chainCompatibility: BlocklistChainCompatibility
  isActived: boolean
  reason: string
}

interface BlocklistImportFormState {
  type: BlocklistType
  matchType: BlocklistMatchType
  values: string
  chainCompatibility: BlocklistChainCompatibility
  isActived: boolean
  reason: string
}

interface BlocklistImportResultState {
  createdCount: number
  updatedCount: number
  skippedCount: number
  errors: BlocklistImportError[]
}

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '-'
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleString()
}

export function SystemConfigPage() {
  const { t } = useTranslation()
  const toast = useToast()
  const { openPrivacyPreferences } = usePrivacyPreferencesModal()
  const { currentEnv, setNetworkEnvironment } = useNetworkEnvironment()

  const cardBg = useColorModeValue('white', 'gray.700')
  const networkValueColor = currentEnv === 'mainnet' ? 'blue.500' : 'red.500'
  const testnetLabel = t('systemConfig.general.networkTestnet')
  const mainnetLabel = t('systemConfig.general.networkMainnet')

  const isMainnet = currentEnv === 'mainnet'
  const TAB_STORAGE_KEY = useMemo(
    () => withNetworkStorageKey('admin_portal_system_config_tab', currentEnv),
    [currentEnv]
  )
  const { activeTabIndex: tabIndex, handleTabChange } = useAdminTabQuery({
    tabs: SYSTEM_CONFIG_TABS,
    defaultTab: 'onchain_policies',
    storageKey: TAB_STORAGE_KEY,
  })

  const [blocklistFilters, setBlocklistFilters] = useState({
    type: 'all' as 'all' | BlocklistType,
    isActived: 'all' as 'all' | 'active' | 'inactive',
    limit: 100,
  })

  const blocklistQueryParams = useMemo(
    () => ({
      type: blocklistFilters.type === 'all' ? undefined : blocklistFilters.type,
      isActived:
        blocklistFilters.isActived === 'all'
          ? undefined
          : blocklistFilters.isActived === 'active',
      limit: blocklistFilters.limit,
    }),
    [blocklistFilters]
  )

  const {
    data: blocklistData,
    isLoading: blocklistLoading,
    isFetching: blocklistFetching,
    refetch: refetchBlocklist,
  } = useBlocklistEntries(blocklistQueryParams, {
    enabled: isMainnet && tabIndex === 10,
  })

  const createBlocklistMutation = useCreateBlocklistEntry()
  const updateBlocklistMutation = useUpdateBlocklistEntry()
  const deleteBlocklistMutation = useDeleteBlocklistEntry()
  const importBlocklistMutation = useImportBlocklistEntries()

  const {
    isOpen: isBlocklistModalOpen,
    onOpen: openBlocklistModal,
    onClose: closeBlocklistModal,
  } = useDisclosure()
  const {
    isOpen: isBlocklistImportModalOpen,
    onOpen: openBlocklistImportModal,
    onClose: closeBlocklistImportModal,
  } = useDisclosure()
  const [editingBlocklistEntry, setEditingBlocklistEntry] =
    useState<BlocklistEntry | null>(null)
  const [blocklistForm, setBlocklistForm] = useState<BlocklistFormState>({
    type: 'user_email',
    matchType: 'exact',
    value: '',
    chainCompatibility: 'evm',
    isActived: true,
    reason: '',
  })
  const [blocklistImportForm, setBlocklistImportForm] =
    useState<BlocklistImportFormState>({
      type: 'user_email',
      matchType: 'exact',
      values: '',
      chainCompatibility: 'evm',
      isActived: true,
      reason: '',
    })
  const [blocklistImportResult, setBlocklistImportResult] =
    useState<BlocklistImportResultState | null>(null)
  const [isExportingBlocklist, setIsExportingBlocklist] = useState(false)
  useEffect(() => {
    if (isMainnet) {
      return
    }
    closeBlocklistModal()
    closeBlocklistImportModal()
    setEditingBlocklistEntry(null)
  }, [isMainnet, closeBlocklistImportModal, closeBlocklistModal])

  const blocklistEntries = blocklistData?.entries ?? []

  const blocklistTypeOptions = useMemo(
    () => [
      { value: 'all', label: t('common.all') },
      {
        value: 'user_email',
        label: t('systemConfig.blocklist.types.userEmail'),
      },
      {
        value: 'merchant_address',
        label: t('systemConfig.blocklist.types.merchantAddress'),
      },
      {
        value: 'merchant_name',
        label: t('systemConfig.blocklist.types.merchantName'),
      },
      {
        value: 'payment_address',
        label: t('systemConfig.blocklist.types.paymentAddress'),
      },
    ],
    [t]
  )

  const blocklistStatusOptions = useMemo(
    () => [
      { value: 'all', label: t('common.all') },
      { value: 'active', label: t('common.active') },
      { value: 'inactive', label: t('common.inactive') },
    ],
    [t]
  )

  const blocklistMatchOptions = useMemo(
    () => [
      { value: 'exact', label: t('systemConfig.blocklist.match.exact') },
      { value: 'contains', label: t('systemConfig.blocklist.match.contains') },
    ],
    [t]
  )

  const blocklistChainOptions = useMemo(
    () => [
      { value: 'evm', label: t('systemConfig.blocklist.chain.evm') },
      { value: 'tron', label: t('systemConfig.blocklist.chain.tron') },
      { value: 'solana', label: t('systemConfig.blocklist.chain.solana') },
    ],
    [t]
  )

  const blocklistTypeLabelMap = useMemo(
    () => ({
      user_email: t('systemConfig.blocklist.types.userEmail'),
      merchant_address: t('systemConfig.blocklist.types.merchantAddress'),
      merchant_name: t('systemConfig.blocklist.types.merchantName'),
      payment_address: t('systemConfig.blocklist.types.paymentAddress'),
    }),
    [t]
  )

  const blocklistMatchLabelMap = useMemo(
    () => ({
      exact: t('systemConfig.blocklist.match.exact'),
      contains: t('systemConfig.blocklist.match.contains'),
    }),
    [t]
  )

  const blocklistChainLabelMap = useMemo(
    () => ({
      evm: t('systemConfig.blocklist.chain.evm'),
      tron: t('systemConfig.blocklist.chain.tron'),
      solana: t('systemConfig.blocklist.chain.solana'),
    }),
    [t]
  )

  const blocklistValuePlaceholder = useMemo(() => {
    switch (blocklistForm.type) {
      case 'user_email':
        return t('systemConfig.blocklist.placeholders.userEmail')
      case 'merchant_address':
        return t('systemConfig.blocklist.placeholders.merchantAddress')
      case 'merchant_name':
        return t('systemConfig.blocklist.placeholders.merchantName')
      case 'payment_address':
        return t('systemConfig.blocklist.placeholders.paymentAddress')
      default:
        return ''
    }
  }, [blocklistForm.type, t])

  const blocklistImportValuePlaceholder = useMemo(() => {
    switch (blocklistImportForm.type) {
      case 'user_email':
        return t('systemConfig.blocklist.placeholders.userEmail')
      case 'merchant_address':
        return t('systemConfig.blocklist.placeholders.merchantAddress')
      case 'merchant_name':
        return t('systemConfig.blocklist.placeholders.merchantName')
      case 'payment_address':
        return t('systemConfig.blocklist.placeholders.paymentAddress')
      default:
        return ''
    }
  }, [blocklistImportForm.type, t])

  const isBlocklistSaving =
    createBlocklistMutation.isPending || updateBlocklistMutation.isPending

  const resetBlocklistImportForm = useCallback(() => {
    setBlocklistImportForm({
      type:
        blocklistFilters.type === 'all' ? 'user_email' : blocklistFilters.type,
      matchType:
        blocklistFilters.type === 'merchant_name' ? 'contains' : 'exact',
      values: '',
      chainCompatibility: 'evm',
      isActived: true,
      reason: '',
    })
    setBlocklistImportResult(null)
  }, [blocklistFilters.type])

  const resetBlocklistForm = useCallback((entry?: BlocklistEntry | null) => {
    if (entry) {
      const enforcedMatchType =
        entry.type === 'merchant_name' ? 'contains' : 'exact'
      setBlocklistForm({
        type: entry.type,
        matchType: enforcedMatchType,
        value: entry.value,
        chainCompatibility: entry.chainCompatibility ?? 'evm',
        isActived: entry.isActived,
        reason: entry.reason ?? '',
      })
      return
    }

    setBlocklistForm({
      type: 'user_email',
      matchType: 'exact',
      value: '',
      chainCompatibility: 'evm',
      isActived: true,
      reason: '',
    })
  }, [])

  const handleCreateBlocklist = useCallback(() => {
    setEditingBlocklistEntry(null)
    resetBlocklistForm(null)
    openBlocklistModal()
  }, [openBlocklistModal, resetBlocklistForm])

  const handleOpenBlocklistImport = useCallback(() => {
    resetBlocklistImportForm()
    openBlocklistImportModal()
  }, [openBlocklistImportModal, resetBlocklistImportForm])

  const handleEditBlocklist = useCallback(
    (entry: BlocklistEntry) => {
      setEditingBlocklistEntry(entry)
      resetBlocklistForm(entry)
      openBlocklistModal()
    },
    [openBlocklistModal, resetBlocklistForm]
  )

  const handleCloseBlocklistModal = useCallback(() => {
    closeBlocklistModal()
    setEditingBlocklistEntry(null)
  }, [closeBlocklistModal])

  const handleCloseBlocklistImport = useCallback(() => {
    closeBlocklistImportModal()
    resetBlocklistImportForm()
  }, [closeBlocklistImportModal, resetBlocklistImportForm])

  const blocklistColumns = useMemo<ColumnConfig<BlocklistEntry>[]>(
    () => [
      {
        key: 'type',
        title: t('systemConfig.blocklist.columns.type'),
        width: '16%',
        render: (_, row) => blocklistTypeLabelMap[row.type] ?? row.type,
      },
      {
        key: 'matchType',
        title: t('systemConfig.blocklist.columns.matchType'),
        width: '12%',
        render: (_, row) =>
          blocklistMatchLabelMap[row.matchType] ?? row.matchType,
      },
      {
        key: 'value',
        title: t('systemConfig.blocklist.columns.value'),
        render: (_, row) => (
          <Text noOfLines={1} title={row.value}>
            {row.value}
          </Text>
        ),
      },
      {
        key: 'chainCompatibility',
        title: t('systemConfig.blocklist.columns.chain'),
        width: '12%',
        render: (_, row) =>
          row.chainCompatibility
            ? blocklistChainLabelMap[row.chainCompatibility]
            : '-',
      },
      {
        key: 'isActived',
        title: t('systemConfig.blocklist.columns.status'),
        width: '10%',
        render: (_, row) => (
          <Badge colorScheme={row.isActived ? 'green' : 'gray'}>
            {row.isActived
              ? t('common.active', { defaultValue: 'Active' })
              : t('common.inactive', { defaultValue: 'Inactive' })}
          </Badge>
        ),
        align: 'center',
      },
      {
        key: 'updatedAt',
        title: t('systemConfig.blocklist.columns.updatedAt'),
        width: '14%',
        render: (_, row) => formatDateTime(row.updatedAt),
        noWrap: true,
      },
    ],
    [blocklistChainLabelMap, blocklistMatchLabelMap, blocklistTypeLabelMap, t]
  )

  const blocklistActions = useCallback<(row: BlocklistEntry) => ActionButton[]>(
    row => [
      {
        label: t('common.edit'),
        onClick: () => handleEditBlocklist(row),
        size: 'xs',
        variant: 'outline',
        colorScheme: 'blue',
        icon: <FiEdit />,
      },
      {
        label: t('common.delete', { defaultValue: 'Delete' }),
        onClick: () => {
          void (async () => {
            try {
              await deleteBlocklistMutation.mutateAsync(row.id)
              toast.success({
                title: t('systemConfig.blocklist.deleteSuccess'),
              })
              await refetchBlocklist()
            } catch (error) {
              toast.error({
                title: t('systemConfig.blocklist.deleteFailed'),
                description:
                  error instanceof Error ? error.message : String(error),
              })
            }
          })()
        },
        size: 'xs',
        variant: 'outline',
        colorScheme: 'red',
        icon: <FiTrash2 />,
        confirmMessage: t('systemConfig.blocklist.confirmDelete', {
          value: row.value,
        }),
        disabled: deleteBlocklistMutation.isPending,
      },
    ],
    [deleteBlocklistMutation, handleEditBlocklist, refetchBlocklist, t, toast]
  )

  const handleEnvToggle = () => {
    const nextEnv = currentEnv === 'mainnet' ? 'testnet' : 'mainnet'
    setNetworkEnvironment(nextEnv)
  }

  const handleBlocklistFilterChange = (
    key: 'type' | 'isActived' | 'limit',
    value: string
  ) => {
    setBlocklistFilters(prev => ({
      ...prev,
      [key]: key === 'limit' ? Number(value) : value,
    }))
  }

  const handleResetBlocklistFilters = () => {
    setBlocklistFilters({
      type: 'all',
      isActived: 'all',
      limit: 100,
    })
  }

  const handleBlocklistTypeChange = (value: BlocklistType) => {
    setBlocklistForm(prev => {
      const nextType = value
      const nextMatchType = nextType === 'merchant_name' ? 'contains' : 'exact'
      const nextChain =
        nextType === 'merchant_address' || nextType === 'payment_address'
          ? prev.chainCompatibility
          : 'evm'
      return {
        ...prev,
        type: nextType,
        matchType: nextMatchType,
        chainCompatibility: nextChain,
      }
    })
  }

  const handleBlocklistImportTypeChange = (value: BlocklistType) => {
    setBlocklistImportForm(prev => {
      const nextType = value
      const nextMatchType = nextType === 'merchant_name' ? 'contains' : 'exact'
      const nextChain =
        nextType === 'merchant_address' || nextType === 'payment_address'
          ? prev.chainCompatibility
          : 'evm'
      return {
        ...prev,
        type: nextType,
        matchType: nextMatchType,
        chainCompatibility: nextChain,
      }
    })
  }

  const handleBlocklistInputChange =
    (field: keyof BlocklistFormState) =>
    (
      event: ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) => {
      const { value } = event.target
      setBlocklistForm(prev => ({ ...prev, [field]: value }))
    }

  const handleBlocklistToggle = (value: boolean) => {
    setBlocklistForm(prev => ({ ...prev, isActived: value }))
  }

  const handleBlocklistImportToggle = (value: boolean) => {
    setBlocklistImportForm(prev => ({ ...prev, isActived: value }))
  }

  const handleSaveBlocklist = async () => {
    const trimmedValue = blocklistForm.value.trim()
    if (!trimmedValue) {
      toast.error({
        title: t('systemConfig.blocklist.validationTitle'),
        description: t('systemConfig.blocklist.validationValueRequired'),
      })
      return
    }

    try {
      const matchType: BlocklistMatchType =
        blocklistForm.type === 'merchant_name' ? 'contains' : 'exact'
      const basePayload = {
        matchType,
        value: trimmedValue,
        chainCompatibility:
          blocklistForm.type === 'merchant_address' ||
          blocklistForm.type === 'payment_address'
            ? blocklistForm.chainCompatibility
            : null,
        isActived: blocklistForm.isActived,
        reason: blocklistForm.reason.trim() || null,
      }

      if (editingBlocklistEntry) {
        await updateBlocklistMutation.mutateAsync({
          entryId: editingBlocklistEntry.id,
          payload: basePayload,
        })
        toast.success({
          title: t('systemConfig.blocklist.updateSuccess'),
        })
      } else {
        await createBlocklistMutation.mutateAsync({
          ...basePayload,
          type: blocklistForm.type,
        })
        toast.success({
          title: t('systemConfig.blocklist.createSuccess'),
        })
      }

      await refetchBlocklist()
      handleCloseBlocklistModal()
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('common.unknownError')
      toast.error({
        title: t('systemConfig.blocklist.saveFailed'),
        description: message,
      })
    }
  }

  const handleImportBlocklist = async () => {
    if (!blocklistImportForm.values.trim()) {
      toast.error({
        title: t('systemConfig.blocklist.validationTitle'),
        description: t('systemConfig.blocklist.validationImportRequired'),
      })
      return
    }

    try {
      const matchType: BlocklistMatchType =
        blocklistImportForm.type === 'merchant_name' ? 'contains' : 'exact'

      const result = await importBlocklistMutation.mutateAsync({
        type: blocklistImportForm.type,
        values: blocklistImportForm.values,
        matchType,
        chainCompatibility:
          blocklistImportForm.type === 'merchant_address' ||
          blocklistImportForm.type === 'payment_address'
            ? blocklistImportForm.chainCompatibility
            : null,
        isActived: blocklistImportForm.isActived,
        reason: blocklistImportForm.reason.trim() || null,
      })
      setBlocklistImportResult({
        createdCount: result.createdCount,
        updatedCount: result.updatedCount,
        skippedCount: result.skippedCount,
        errors: result.errors,
      })
      if (result.errors.length > 0) {
        toast.warning({
          title: t('systemConfig.blocklist.importPartial'),
          description: t('systemConfig.blocklist.importSummary', {
            created: result.createdCount,
            updated: result.updatedCount,
            skipped: result.skippedCount,
          }),
        })
      } else {
        toast.success({
          title: t('systemConfig.blocklist.importSuccess'),
          description: t('systemConfig.blocklist.importSummary', {
            created: result.createdCount,
            updated: result.updatedCount,
            skipped: result.skippedCount,
          }),
        })
      }
      await refetchBlocklist()
      if (result.errors.length === 0) {
        handleCloseBlocklistImport()
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('common.unknownError')
      toast.error({
        title: t('systemConfig.blocklist.importFailed'),
        description: message,
      })
    }
  }

  const handleExportBlocklist = async () => {
    try {
      setIsExportingBlocklist(true)
      const { blob, filename } = await blocklistService.exportCsv(
        blocklistQueryParams,
        currentEnv
      )
      triggerBrowserDownload(blob, filename)
      toast.success({
        title: t('common.export.success'),
        description: t('common.export.downloaded'),
      })
    } catch (error) {
      toast.error({
        title: t('common.export.label'),
        description: error instanceof Error ? error.message : String(error),
      })
    } finally {
      setIsExportingBlocklist(false)
    }
  }

  return (
    <>
      <Box className="system-config-page">
        <VStack align="stretch" spacing={6}>
          <Box>
            <HStack justify="space-between" align="start" mb={2}>
              <Box>
                <Heading size="lg" mb={2}>
                  {t('systemConfig.title')}
                </Heading>
                <Text color="gray.600">{t('systemConfig.subtitle')}</Text>
              </Box>
            </HStack>
          </Box>

          <Tabs
            variant="enclosed"
            colorScheme="admin"
            isLazy
            lazyBehavior="keepMounted"
            index={tabIndex}
            onChange={handleTabChange}
          >
            <TabList overflowX="auto" overflowY="hidden">
              <Tab
                aria-label={t('systemConfig.tabs.onchainPolicies')}
                minW="56px"
                justifyContent="center"
              >
                <Tooltip
                  label={t('systemConfig.tabs.onchainPolicies')}
                  hasArrow
                >
                  <Box display="flex" alignItems="center">
                    <Icon as={FiActivity} boxSize={6} />
                  </Box>
                </Tooltip>
              </Tab>
              <Tab
                aria-label={t('systemConfig.tabs.onchainPolicyOverrides', {
                  defaultValue: 'onchain policy overrides',
                })}
                minW="56px"
                justifyContent="center"
              >
                <Tooltip
                  label={t('systemConfig.tabs.onchainPolicyOverrides', {
                    defaultValue: 'onchain policy overrides',
                  })}
                  hasArrow
                >
                  <Box display="flex" alignItems="center">
                    <Icon as={FiTarget} boxSize={6} />
                  </Box>
                </Tooltip>
              </Tab>
              <Tab
                aria-label={t('systemConfig.tabs.runtimeConfig', {
                  defaultValue: 'runtime config',
                })}
                minW="56px"
                justifyContent="center"
              >
                <Tooltip
                  label={t('systemConfig.tabs.runtimeConfig', {
                    defaultValue: 'runtime config',
                  })}
                  hasArrow
                >
                  <Box display="flex" alignItems="center">
                    <Icon as={FiSliders} boxSize={6} />
                  </Box>
                </Tooltip>
              </Tab>
              <Tab
                aria-label={t('systemConfig.tabs.rpcEndpoints', {
                  defaultValue: 'rpc endpoints',
                })}
                minW="56px"
                justifyContent="center"
              >
                <Tooltip
                  label={t('systemConfig.tabs.rpcEndpoints', {
                    defaultValue: 'rpc endpoints',
                  })}
                  hasArrow
                >
                  <Box display="flex" alignItems="center">
                    <Icon as={FiLink} boxSize={6} />
                  </Box>
                </Tooltip>
              </Tab>
              <Tab
                aria-label={t('systemConfig.tabs.platformWallets', {
                  defaultValue: 'platform wallets',
                })}
                minW="56px"
                justifyContent="center"
              >
                <Tooltip
                  label={t('systemConfig.tabs.platformWallets', {
                    defaultValue: 'platform wallets',
                  })}
                  hasArrow
                >
                  <Box display="flex" alignItems="center">
                    <Icon as={FiCreditCard} boxSize={6} />
                  </Box>
                </Tooltip>
              </Tab>
              <Tab
                aria-label={t('systemConfig.tabs.chains', {
                  defaultValue: 'chains',
                })}
                minW="56px"
                justifyContent="center"
              >
                <Tooltip
                  label={t('systemConfig.tabs.chains', {
                    defaultValue: 'chains',
                  })}
                  hasArrow
                >
                  <Box display="flex" alignItems="center">
                    <Icon as={FiGitBranch} boxSize={6} />
                  </Box>
                </Tooltip>
              </Tab>
              <Tab
                aria-label={t('systemConfig.tabs.tokens', {
                  defaultValue: 'tokens',
                })}
                minW="56px"
                justifyContent="center"
              >
                <Tooltip
                  label={t('systemConfig.tabs.tokens', {
                    defaultValue: 'tokens',
                  })}
                  hasArrow
                >
                  <Box display="flex" alignItems="center">
                    <Icon as={FiDollarSign} boxSize={6} />
                  </Box>
                </Tooltip>
              </Tab>
              <Tab
                aria-label={t('systemConfig.tabs.feePolicy', {
                  defaultValue: 'fee policy',
                })}
                minW="56px"
                justifyContent="center"
              >
                <Tooltip
                  label={t('systemConfig.tabs.feePolicy', {
                    defaultValue: 'fee policy',
                  })}
                  hasArrow
                >
                  <Box display="flex" alignItems="center">
                    <Icon as={FiPercent} boxSize={6} />
                  </Box>
                </Tooltip>
              </Tab>
              <Tab
                aria-label={t('systemConfig.tabs.subscriptionPlans', {
                  defaultValue: 'subscription plans',
                })}
                minW="56px"
                justifyContent="center"
              >
                <Tooltip
                  label={t('systemConfig.tabs.subscriptionPlans', {
                    defaultValue: 'subscription plans',
                  })}
                  hasArrow
                >
                  <Box display="flex" alignItems="center">
                    <Icon as={FiPackage} boxSize={6} />
                  </Box>
                </Tooltip>
              </Tab>
              <Tab
                aria-label={t('systemConfig.tabs.signupEntries')}
                minW="56px"
                justifyContent="center"
              >
                <Tooltip label={t('systemConfig.tabs.signupEntries')} hasArrow>
                  <Box display="flex" alignItems="center">
                    <Icon as={FiUserPlus} boxSize={6} />
                  </Box>
                </Tooltip>
              </Tab>
              <Tab
                aria-label={t('systemConfig.tabs.blocklist')}
                minW="56px"
                justifyContent="center"
              >
                <Tooltip label={t('systemConfig.tabs.blocklist')} hasArrow>
                  <Box display="flex" alignItems="center">
                    <Icon as={FiSlash} boxSize={6} />
                  </Box>
                </Tooltip>
              </Tab>
              <Tab
                aria-label={t('systemConfig.tabs.cooldown')}
                minW="56px"
                justifyContent="center"
              >
                <Tooltip label={t('systemConfig.tabs.cooldown')} hasArrow>
                  <Box display="flex" alignItems="center">
                    <Icon as={FiClock} boxSize={6} />
                  </Box>
                </Tooltip>
              </Tab>
              <Tab
                aria-label={t('systemConfig.tabs.general')}
                minW="56px"
                justifyContent="center"
              >
                <Tooltip label={t('systemConfig.tabs.general')} hasArrow>
                  <Box display="flex" alignItems="center">
                    <Icon as={FiSettings} boxSize={6} />
                  </Box>
                </Tooltip>
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel p={0} pt={4}>
                <OnchainOperationPolicyTab
                  cardBg={cardBg}
                  showBaselinePolicies
                  showPlanOverrides={false}
                />
              </TabPanel>

              <TabPanel p={0} pt={4}>
                <OnchainOperationPolicyTab
                  cardBg={cardBg}
                  showBaselinePolicies={false}
                  showPlanOverrides
                />
              </TabPanel>

              <TabPanel p={0} pt={4}>
                <RuntimeConfigTab cardBg={cardBg} />
              </TabPanel>

              <TabPanel p={0} pt={4}>
                <RpcEndpointsTab cardBg={cardBg} />
              </TabPanel>

              <TabPanel p={0} pt={4}>
                <PlatformWalletsTab cardBg={cardBg} />
              </TabPanel>

              <TabPanel p={0} pt={4}>
                <ChainsTab cardBg={cardBg} />
              </TabPanel>

              <TabPanel p={0} pt={4}>
                <TokensTab cardBg={cardBg} />
              </TabPanel>

              <TabPanel p={0} pt={4}>
                <FeePolicyTab cardBg={cardBg} />
              </TabPanel>

              <TabPanel p={0} pt={4}>
                {isMainnet ? (
                  <SubscriptionPlansTab cardBg={cardBg} />
                ) : (
                  <MainnetOnlyNotice />
                )}
              </TabPanel>

              <TabPanel p={0} pt={4}>
                <MerchantSignupInvitesTab cardBg={cardBg} />
              </TabPanel>

              <TabPanel p={0} pt={4}>
                {isMainnet ? (
                  <VStack spacing={4} align="stretch">
                    <Card bg={cardBg}>
                      <CardHeader>
                        <Heading size="sm">
                          {t('systemConfig.blocklist.filters')}
                        </Heading>
                      </CardHeader>
                      <CardBody pt={0}>
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                          <FormControl>
                            <FormLabel fontSize="xs" mb={1}>
                              {t('systemConfig.blocklist.columns.type')}
                            </FormLabel>
                            <DropdownSelect
                              value={blocklistFilters.type}
                              options={blocklistTypeOptions}
                              onChange={value =>
                                handleBlocklistFilterChange('type', value)
                              }
                              size="xs"
                              maxMenuHeight="280px"
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel fontSize="xs" mb={1}>
                              {t('systemConfig.blocklist.columns.status')}
                            </FormLabel>
                            <DropdownSelect
                              value={blocklistFilters.isActived}
                              options={blocklistStatusOptions}
                              onChange={value =>
                                handleBlocklistFilterChange('isActived', value)
                              }
                              size="xs"
                              maxMenuHeight="280px"
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel fontSize="xs" mb={1}>
                              {t('systemConfig.blocklist.columns.limit')}
                            </FormLabel>
                            <DropdownSelect
                              value={String(blocklistFilters.limit)}
                              options={[50, 100, 200, 500].map(limit => ({
                                value: String(limit),
                                label: String(limit),
                              }))}
                              onChange={value =>
                                handleBlocklistFilterChange('limit', value)
                              }
                              size="xs"
                              maxMenuHeight="280px"
                            />
                          </FormControl>
                        </SimpleGrid>

                        <HStack
                          justify="space-between"
                          mt={3}
                          flexWrap="wrap"
                          gap={3}
                        >
                          <HStack spacing={2}>
                            <RefreshIconButton
                              label={t('common.refresh')}
                              onClick={() => refetchBlocklist()}
                              isLoading={blocklistFetching}
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={handleResetBlocklistFilters}
                            >
                              {t('common.reset')}
                            </Button>
                          </HStack>
                          <HStack spacing={2}>
                            <Text fontSize="sm" color="gray.500">
                              {t('systemConfig.blocklist.total', {
                                value:
                                  blocklistData?.total ??
                                  blocklistEntries.length,
                              })}
                            </Text>
                            <Button
                              size="sm"
                              variant="outline"
                              leftIcon={<FiDownload />}
                              onClick={() => void handleExportBlocklist()}
                              isLoading={isExportingBlocklist}
                            >
                              {t('common.export.label')}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              leftIcon={<FiUpload />}
                              onClick={handleOpenBlocklistImport}
                            >
                              {t('systemConfig.blocklist.import')}
                            </Button>
                            <Button
                              size="sm"
                              colorScheme="admin"
                              leftIcon={<FiPlus />}
                              onClick={handleCreateBlocklist}
                            >
                              {t('systemConfig.blocklist.add')}
                            </Button>
                          </HStack>
                        </HStack>
                      </CardBody>
                    </Card>

                    <Card bg={cardBg}>
                      <CardHeader>
                        <Heading size="md">
                          {t('systemConfig.blocklist.list')}
                        </Heading>
                      </CardHeader>
                      <CardBody pt={0}>
                        <DataTable<BlocklistEntry>
                          data={blocklistEntries}
                          loading={blocklistLoading}
                          refreshing={blocklistFetching}
                          showRefresh
                          onRefresh={() => refetchBlocklist()}
                          columns={blocklistColumns}
                          actions={blocklistActions}
                          actionsColumnWidth="88px"
                          showFilterBar={false}
                          sortable={false}
                          size="sm"
                          emptyMessage={t('systemConfig.blocklist.empty')}
                        />
                      </CardBody>
                    </Card>
                  </VStack>
                ) : (
                  <MainnetOnlyNotice />
                )}
              </TabPanel>

              <TabPanel p={0} pt={4}>
                <CooldownOperationTypesTab cardBg={cardBg} />
              </TabPanel>

              <TabPanel p={0} pt={4}>
                <Grid templateColumns="1fr" gap={4}>
                  <Card bg={cardBg}>
                    <CardHeader>
                      <Heading size="md">
                        {t('systemConfig.general.networkModeTitle')}
                      </Heading>
                    </CardHeader>
                    <CardBody pt={0}>
                      <VStack spacing={3} align="stretch">
                        <Text fontSize="sm" color="gray.500">
                          {t('systemConfig.general.networkModeDescription')}
                        </Text>
                        <Flex align="center" gap={4} flexWrap="nowrap">
                          <Text
                            fontSize="sm"
                            fontWeight="semibold"
                            color={networkValueColor}
                            whiteSpace="nowrap"
                            visibility={
                              currentEnv === 'testnet' ? 'visible' : 'hidden'
                            }
                          >
                            {testnetLabel}
                          </Text>
                          <Tooltip
                            label={
                              currentEnv === 'mainnet'
                                ? t(
                                    'systemConfig.general.switchToTestnetTooltip'
                                  )
                                : t(
                                    'systemConfig.general.switchToMainnetTooltip'
                                  )
                            }
                            placement="bottom"
                            hasArrow
                          >
                            <Box position="relative">
                              <Switch
                                isChecked={currentEnv === 'mainnet'}
                                onChange={handleEnvToggle}
                                aria-label={t(
                                  'systemConfig.general.environment'
                                )}
                                sx={{
                                  '.chakra-switch__track': {
                                    width: '64px',
                                    height: '26px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '2px',
                                    borderRadius: 'full',
                                    bg: '#fc5c65',
                                    transition: 'background-color 0.2s ease',
                                  },
                                  '.chakra-switch__thumb': {
                                    width: '22px',
                                    height: '22px',
                                    bg: 'white',
                                    boxShadow: 'md',
                                    transform: 'translateX(0px)',
                                    transition: 'transform 0.2s ease',
                                  },
                                }}
                                _checked={{
                                  '.chakra-switch__track': {
                                    bg: '#45AAF2',
                                  },
                                  '.chakra-switch__thumb': {
                                    transform: 'translateX(38px)',
                                  },
                                }}
                              />
                            </Box>
                          </Tooltip>
                          <Text
                            fontSize="sm"
                            fontWeight="semibold"
                            color={networkValueColor}
                            whiteSpace="nowrap"
                            visibility={
                              currentEnv === 'mainnet' ? 'visible' : 'hidden'
                            }
                          >
                            {mainnetLabel}
                          </Text>
                        </Flex>
                      </VStack>
                    </CardBody>
                  </Card>
                  <Card bg={cardBg}>
                    <CardHeader>
                      <Heading size="md">
                        {t('privacyPreferences.cardTitle')}
                      </Heading>
                    </CardHeader>
                    <CardBody pt={0}>
                      <VStack spacing={3} align="stretch">
                        <Text fontSize="sm" color="gray.500">
                          {t('privacyPreferences.cardDescription')}
                        </Text>
                        <Button
                          size="sm"
                          alignSelf="flex-start"
                          onClick={openPrivacyPreferences}
                        >
                          {t('privacyPreferences.manageEntry')}
                        </Button>
                      </VStack>
                    </CardBody>
                  </Card>
                </Grid>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>

        <Modal
          isOpen={isBlocklistModalOpen}
          onClose={handleCloseBlocklistModal}
          size="lg"
        >
          <ModalOverlay />
          <ModalContent className="system-config-dialog">
            <ModalHeader fontSize="lg" py={4}>
              {editingBlocklistEntry
                ? t('systemConfig.blocklist.editTitle')
                : t('systemConfig.blocklist.createTitle')}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={3} align="stretch">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" mb={1.5}>
                    {t('systemConfig.blocklist.columns.type')}
                  </FormLabel>
                  <DropdownSelect
                    value={blocklistForm.type}
                    options={blocklistTypeOptions.filter(
                      option => option.value !== 'all'
                    )}
                    onChange={value =>
                      handleBlocklistTypeChange(value as BlocklistType)
                    }
                    isDisabled={Boolean(editingBlocklistEntry)}
                    size="xs"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" mb={1.5}>
                    {t('systemConfig.blocklist.columns.matchType')}
                  </FormLabel>
                  <DropdownSelect
                    value={blocklistForm.matchType}
                    options={blocklistMatchOptions}
                    onChange={value =>
                      setBlocklistForm(prev => ({
                        ...prev,
                        matchType: value as BlocklistMatchType,
                      }))
                    }
                    isDisabled
                    size="xs"
                  />
                  <FormHelperText fontSize="xs">
                    {blocklistForm.type === 'merchant_name'
                      ? t('systemConfig.blocklist.match.lockedContains')
                      : t('systemConfig.blocklist.match.lockedExact')}
                  </FormHelperText>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" mb={1.5}>
                    {t('systemConfig.blocklist.columns.value')}
                  </FormLabel>
                  <Input
                    size="sm"
                    value={blocklistForm.value}
                    onChange={handleBlocklistInputChange('value')}
                    placeholder={blocklistValuePlaceholder}
                  />
                </FormControl>

                {(blocklistForm.type === 'merchant_address' ||
                  blocklistForm.type === 'payment_address') && (
                  <FormControl>
                    <FormLabel fontSize="sm" mb={1.5}>
                      {t('systemConfig.blocklist.columns.chain')}
                    </FormLabel>
                    <DropdownSelect
                      value={blocklistForm.chainCompatibility}
                      options={blocklistChainOptions}
                      onChange={value =>
                        setBlocklistForm(prev => ({
                          ...prev,
                          chainCompatibility:
                            value as BlocklistChainCompatibility,
                        }))
                      }
                      size="xs"
                    />
                  </FormControl>
                )}

                <FormControl>
                  <FormLabel fontSize="sm" mb={1.5}>
                    {t('systemConfig.blocklist.columns.status')}
                  </FormLabel>
                  <HStack spacing={3}>
                    <Switch
                      isChecked={blocklistForm.isActived}
                      onChange={event =>
                        handleBlocklistToggle(event.target.checked)
                      }
                    />
                    <Text fontSize="sm">
                      {blocklistForm.isActived
                        ? t('common.active')
                        : t('common.inactive')}
                    </Text>
                  </HStack>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" mb={1.5}>
                    {t('systemConfig.blocklist.columns.reason')}
                  </FormLabel>
                  <Textarea
                    size="sm"
                    value={blocklistForm.reason}
                    onChange={handleBlocklistInputChange('reason')}
                    placeholder={t(
                      'systemConfig.blocklist.placeholders.reason'
                    )}
                  />
                </FormControl>
                {blocklistImportResult && (
                  <VStack
                    align="stretch"
                    spacing={1.5}
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    p={3}
                  >
                    <Text fontSize="sm" fontWeight="semibold">
                      {t('systemConfig.blocklist.importResultTitle')}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {t('systemConfig.blocklist.importSummary', {
                        created: blocklistImportResult.createdCount,
                        updated: blocklistImportResult.updatedCount,
                        skipped: blocklistImportResult.skippedCount,
                      })}
                    </Text>
                    {blocklistImportResult.errors.length > 0 && (
                      <VStack
                        align="stretch"
                        spacing={1}
                        maxH="180px"
                        overflowY="auto"
                      >
                        {blocklistImportResult.errors.map(error => (
                          <Text
                            key={`${error.lineNumber}-${error.value}`}
                            fontSize="xs"
                            color="red.500"
                          >
                            {t('systemConfig.blocklist.importErrorLine', {
                              line: error.lineNumber,
                              value: error.value,
                              message: error.message,
                            })}
                          </Text>
                        ))}
                      </VStack>
                    )}
                  </VStack>
                )}
              </VStack>
            </ModalBody>
            <ModalFooter pt={2}>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCloseBlocklistModal}
              >
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                colorScheme="admin"
                ml={3}
                onClick={handleSaveBlocklist}
                isLoading={isBlocklistSaving}
              >
                {t('common.save')}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        <Modal
          isOpen={isBlocklistImportModalOpen}
          onClose={handleCloseBlocklistImport}
          size="lg"
        >
          <ModalOverlay />
          <ModalContent className="system-config-dialog">
            <ModalHeader fontSize="lg" py={4}>
              {t('systemConfig.blocklist.importTitle')}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={3} align="stretch">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" mb={1.5}>
                    {t('systemConfig.blocklist.columns.type')}
                  </FormLabel>
                  <DropdownSelect
                    value={blocklistImportForm.type}
                    options={blocklistTypeOptions.filter(
                      option => option.value !== 'all'
                    )}
                    onChange={value =>
                      handleBlocklistImportTypeChange(value as BlocklistType)
                    }
                    size="xs"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" mb={1.5}>
                    {t('systemConfig.blocklist.columns.matchType')}
                  </FormLabel>
                  <DropdownSelect
                    value={blocklistImportForm.matchType}
                    options={blocklistMatchOptions}
                    onChange={() => {}}
                    isDisabled
                    size="xs"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" mb={1.5}>
                    {t('systemConfig.blocklist.importValues')}
                  </FormLabel>
                  <Textarea
                    size="sm"
                    minH="220px"
                    value={blocklistImportForm.values}
                    onChange={event =>
                      setBlocklistImportForm(prev => ({
                        ...prev,
                        values: event.target.value,
                      }))
                    }
                    placeholder={t('systemConfig.blocklist.importPlaceholder', {
                      value: blocklistImportValuePlaceholder,
                    })}
                  />
                  <FormHelperText fontSize="xs">
                    {t('systemConfig.blocklist.importHint')}
                  </FormHelperText>
                </FormControl>

                {(blocklistImportForm.type === 'merchant_address' ||
                  blocklistImportForm.type === 'payment_address') && (
                  <FormControl>
                    <FormLabel fontSize="sm" mb={1.5}>
                      {t('systemConfig.blocklist.columns.chain')}
                    </FormLabel>
                    <DropdownSelect
                      value={blocklistImportForm.chainCompatibility}
                      options={blocklistChainOptions}
                      onChange={value =>
                        setBlocklistImportForm(prev => ({
                          ...prev,
                          chainCompatibility:
                            value as BlocklistChainCompatibility,
                        }))
                      }
                      size="xs"
                    />
                  </FormControl>
                )}

                <FormControl>
                  <FormLabel fontSize="sm" mb={1.5}>
                    {t('systemConfig.blocklist.columns.status')}
                  </FormLabel>
                  <HStack spacing={3}>
                    <Switch
                      isChecked={blocklistImportForm.isActived}
                      onChange={event =>
                        handleBlocklistImportToggle(event.target.checked)
                      }
                    />
                    <Text fontSize="sm">
                      {blocklistImportForm.isActived
                        ? t('common.active')
                        : t('common.inactive')}
                    </Text>
                  </HStack>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" mb={1.5}>
                    {t('systemConfig.blocklist.columns.reason')}
                  </FormLabel>
                  <Textarea
                    size="sm"
                    value={blocklistImportForm.reason}
                    onChange={event =>
                      setBlocklistImportForm(prev => ({
                        ...prev,
                        reason: event.target.value,
                      }))
                    }
                    placeholder={t(
                      'systemConfig.blocklist.placeholders.reason'
                    )}
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter pt={2}>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCloseBlocklistImport}
              >
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                colorScheme="admin"
                ml={3}
                onClick={handleImportBlocklist}
                isLoading={importBlocklistMutation.isPending}
              >
                {t('systemConfig.blocklist.import')}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </>
  )
}
